/**
 * Address-grammar authoring (ADR 228).
 *
 * The backend reads a warehouse's address convention as a Java regex with named
 * groups `aisle` / `level` / `space` (ADR 188). That is the right storage format —
 * it is precise, and the server must not guess — but it is an impossible thing to
 * ask a warehouse supervisor to write, and getting it wrong is silent: the import
 * succeeds, every aisle label comes out null, and the structure derives to nothing.
 *
 * So the pattern is BUILT here from an example. The operator marks, on one real
 * address, which characters are the aisle, the level and the position; this module
 * turns those marks into the regex and shows what it extracts. Nobody types a
 * backslash.
 */

/** What one character of the sample address means. `ignore` still has to MATCH —
 *  it is "some character of this kind", not "skip" — or the anchors would fail. */
export type Segment = "aisle" | "level" | "space" | "ignore";

export interface Extracted {
  aisle: string | null;
  level: string | null;
  space: string | null;
}

/** A run of consecutive characters carrying the same mark. */
interface Run {
  segment: Segment;
  chars: string;
}

function runs(sample: string, marks: Segment[]): Run[] {
  const out: Run[] = [];
  for (let i = 0; i < sample.length; i++) {
    const segment = marks[i] ?? "ignore";
    const last = out[out.length - 1];
    if (last && last.segment === segment) last.chars += sample[i];
    else out.push({ segment, chars: sample[i] });
  }
  return out;
}

const isDigit = (c: string) => c >= "0" && c <= "9";
const isUpper = (c: string) => c >= "A" && c <= "Z";
const isLower = (c: string) => c >= "a" && c <= "z";

/** Escape a literal for use inside a regex. */
function escapeLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\/-]/g, "\\$&");
}

/**
 * The character class a run should match, widened from the example.
 *
 * Widening is the whole point: the sample shows `06M0651`, but the pattern has to
 * match every address in the file, so the `065` becomes "three digits", not the
 * literal 065. Separators are the exception — a `-` in the sample is a `-` in
 * every address, and matching it literally is both correct and readable.
 */
function classOf(chars: string): string {
  const cs = [...chars];
  if (cs.every(isDigit)) return "\\d";
  if (cs.every(isUpper)) return "[A-Z]";
  if (cs.every(isLower)) return "[a-z]";
  if (cs.every((c) => isUpper(c) || isLower(c))) return "[A-Za-z]";
  if (cs.every((c) => isDigit(c) || isUpper(c) || isLower(c))) return "[A-Za-z0-9]";
  // A run of one repeated punctuation character (a separator) matches literally.
  if (cs.every((c) => c === cs[0])) return escapeLiteral(cs[0]);
  return ".";
}

function quantify(cls: string, n: number): string {
  return n === 1 ? cls : `${cls}{${n}}`;
}

/**
 * Build the anchored regex for a sample address and its per-character marks.
 * Returns null when nothing is marked — an all-`ignore` pattern would match but
 * name no groups, which is exactly the silent no-op this feature exists to prevent.
 */
export function buildPattern(sample: string, marks: Segment[]): string | null {
  if (!sample) return null;
  if (!marks.some((m) => m === "aisle" || m === "level" || m === "space")) return null;

  let out = "^";
  for (const run of runs(sample, marks)) {
    const body = quantify(classOf(run.chars), run.chars.length);
    if (run.segment === "ignore") out += body;
    // A named group wraps the same body; the group is what the server reads.
    else out += `(?<${run.segment}>${body})`;
  }
  return out + "$";
}

/**
 * Run a pattern against an address, the way the server will.
 *
 * JS `RegExp` accepts the same `(?<name>…)` syntax Java uses, so the preview is a
 * true dry run rather than an approximation — for these patterns, which use only
 * classes, quantifiers and named groups, the two engines agree. Returns null when
 * the pattern does not compile or does not match.
 */
export function applyPattern(pattern: string, address: string): Extracted | null {
  let re: RegExp;
  try {
    re = new RegExp(pattern);
  } catch {
    return null;
  }
  const m = re.exec(address);
  if (!m) return null;
  const g = m.groups ?? {};
  return {
    aisle: g.aisle ?? null,
    level: g.level ?? null,
    space: g.space ?? null,
  };
}

/** How many of `addresses` the pattern matches — the honest measure of whether the
 *  grammar fits the file, shown before the operator commits to an import. */
export function matchRate(pattern: string, addresses: string[]): { matched: number; total: number } {
  let matched = 0;
  for (const a of addresses) if (applyPattern(pattern, a)) matched++;
  return { matched, total: addresses.length };
}

/**
 * How many distinct slots the matched addresses resolve to.
 *
 * Match rate alone cannot tell a correct grammar from a plausible wrong one: mark
 * the level digits of `JY01-00A` as the position and the pattern still matches
 * 100% of the file, but every address on a level collapses onto five slots. The
 * invariant a grammar must hold is that (aisle, level, space) IDENTIFIES an
 * address — `distinct < matched` means two bins share a location, which is the
 * shape of a mis-marked sample.
 */
export function slotCount(pattern: string, addresses: string[]): { matched: number; distinct: number } {
  const slots = new Set<string>();
  let matched = 0;
  for (const a of addresses) {
    const x = applyPattern(pattern, a);
    if (!x) continue;
    matched++;
    slots.add(`${x.aisle ?? ""} ${x.level ?? ""} ${x.space ?? ""}`);
  }
  return { matched, distinct: slots.size };
}

/**
 * Marks a pattern implies on one address — the inverse of `buildPattern`, as far
 * as it can be taken.
 *
 * A regex cannot be turned back into marks in general, but it does not have to be:
 * matching it against a real address with the `d` flag yields each named group's
 * offsets, which is exactly where the aisle, level and position sit in THAT
 * address. Enough to show a remembered grammar on a new file's sample and let the
 * operator adjust it by clicking. Null when it does not match this address.
 */
export function marksFromPattern(pattern: string, address: string): Segment[] | null {
  let m: RegExpExecArray | null;
  try {
    m = new RegExp(pattern, "d").exec(address);
  } catch {
    return null;
  }
  if (!m) return null;
  const spans = (m as { indices?: { groups?: Record<string, [number, number] | undefined> } })
    .indices?.groups;
  if (!spans) return null;
  const marks = blankMarks(address);
  for (const seg of ["aisle", "level", "space"] as const) {
    const span = spans[seg];
    if (!span) continue;
    for (let i = span[0]; i < span[1]; i++) marks[i] = seg;
  }
  return marks;
}

export interface Grammar {
  pattern: string;
  marks: Segment[];
  /** Where it came from, so the panel can say which and the operator can tell. */
  source: "remembered" | "guess" | "none";
}

/** Ranks a candidate: how much of the file it reads, then how finely it separates it. */
function score(pattern: string, addresses: string[]): [number, number] {
  const { matched, distinct } = slotCount(pattern, addresses);
  return [matched, distinct];
}

/**
 * The grammar to open the import form with (ADR 231).
 *
 * A warehouse exported as one zones.json per level asks the same question four
 * times, so the convention it was taught last time is the first candidate. It only
 * wins where it is at least as good as the suggestion on THIS file — measured, not
 * assumed, because the next file may come from a different WMS. It also has to
 * match the displayed sample, so the character strip shows the truth rather than a
 * grammar the operator cannot see.
 */
export function chooseGrammar(
  sample: string, addresses: string[], remembered?: string | null,
): Grammar {
  const guessed = buildPattern(sample, guessMarks(sample, addresses));
  const guess: Grammar = guessed
    ? { pattern: guessed, marks: guessMarks(sample, addresses), source: "guess" }
    : { pattern: "", marks: blankMarks(sample), source: "none" };

  if (!remembered || !sample) return guess;
  const recalled = marksFromPattern(remembered, sample);
  if (!recalled) return guess;

  const [rm, rd] = score(remembered, addresses);
  const [gm, gd] = guessed ? score(guessed, addresses) : [0, 0];
  if (rm > gm || (rm === gm && rd >= gd)) {
    return { pattern: remembered, marks: recalled, source: "remembered" };
  }
  return guess;
}

/** Marks that leave every character ignored — the builder's starting state. */
export function blankMarks(sample: string): Segment[] {
  return Array.from({ length: sample.length }, () => "ignore" as Segment);
}

interface Span { start: number; len: number }

const end = (s: Span) => s.start + s.len;

/** `r` with `cut` removed — 0, 1 or 2 surviving pieces. */
function subtract(r: Span, cut: Span | null): Span[] {
  if (!cut || end(cut) <= r.start || cut.start >= end(r)) return [r];
  return [
    { start: r.start, len: cut.start - r.start },
    { start: end(cut), len: end(r) - end(cut) },
  ].filter((p) => p.len > 0);
}

/**
 * The digit field that never varies across the file — the level, when the file is
 * a per-level export.
 *
 * iC3 exports one zones.json per level (`Flex_JY_Nivel_2` → every address carries
 * `02`), so the data NAMES the level instead of leaving it to a convention: the
 * level is the field that is constant while the positions vary.
 *
 * A whole digit FIELD has to be constant, not merely some characters of one. That
 * is what separates a level from zero padding: in `06M0011` the leading `0` of the
 * bay never changes either, but its field (`001`, `030`, …) does, so the field is
 * a position and the `0` is padding. Only fields after the aisle are considered,
 * which drops a fixed building prefix. Needs same-length peers to compare by index.
 */
function constantDigitField(sample: string, addresses: string[], runs: Span[]): Span | null {
  const peers = addresses.filter((a) => a.length === sample.length);
  if (peers.length < 2 || peers.every((a) => a === sample)) return null;

  const fixed = [...sample].map((c, i) => peers.every((a) => a[i] === c));
  return runs.find((r) => {
    for (let i = r.start; i < end(r); i++) if (!fixed[i]) return false;
    return true;
  }) ?? null;
}

/**
 * A first guess at the marks, so the operator corrects rather than starts blank.
 *
 * The heuristic reads the shape most WMS conventions share: a letter run is the
 * aisle, the level is the digit field the file holds constant (falling back to a
 * trailing digit), and the longest remaining digit run is the position. It is
 * deliberately conservative — when the shape does not fit it marks nothing rather
 * than inventing a grammar, because a wrong guess the operator accepts without
 * reading is worse than an empty form.
 *
 * Passing the file's other addresses is what makes the level reliable; without
 * them only the positional fallback applies.
 */
export function guessMarks(sample: string, addresses: string[] = []): Segment[] {
  const marks = blankMarks(sample);
  if (!sample) return marks;

  const letterRuns: Span[] = [];
  const digitRuns: Span[] = [];
  for (let i = 0; i < sample.length; ) {
    const c = sample[i];
    const isL = isUpper(c) || isLower(c);
    const isD = isDigit(c);
    if (!isL && !isD) { i++; continue; }
    let j = i;
    while (j < sample.length && (isL ? (isUpper(sample[j]) || isLower(sample[j])) : isDigit(sample[j]))) j++;
    (isL ? letterRuns : digitRuns).push({ start: i, len: j - i });
    i = j;
  }

  // Aisle: the first letter run. No letters anywhere (a purely numeric scheme)
  // means the shape does not fit — leave it to the operator.
  if (letterRuns.length === 0) return marks;
  const aisle = letterRuns[0];
  for (let i = aisle.start; i < aisle.start + aisle.len; i++) marks[i] = "aisle";

  const after = digitRuns.filter((r) => r.start > aisle.start);
  if (after.length === 0) return marks;

  // Level, preferred: the constant field. Fallback: the FINAL digit, when the
  // address ends in digits — most conventions that encode a level put it last, and
  // in a dash-less scheme (`06M0651`) it is not a run of its own but the last
  // character of one, so the run has to be split.
  const constant = constantDigitField(sample, addresses, after);
  const last = after[after.length - 1];
  const level = constant
    ?? (end(last) === sample.length ? { start: end(last) - 1, len: 1 } : null);
  if (level) for (let i = level.start; i < end(level); i++) marks[i] = "level";

  // Position: the longest digit run after the aisle, minus whatever the level took.
  const forSpace = after.flatMap((r) => subtract(r, level));
  if (forSpace.length === 0) return marks;
  const space = forSpace.reduce((a, b) => (b.len > a.len ? b : a));
  for (let i = space.start; i < end(space); i++) marks[i] = "space";

  // A position often carries a varying letter suffix — `00A`/`00B` are the two
  // sides of bay 00, and dropping the letter would collapse them onto one slot.
  // Absorb neighbouring alphanumerics that VARY across the file; a constant
  // neighbour belongs to some fixed prefix, not to this position.
  const peers = addresses.filter((a) => a.length === sample.length);
  const varies = (i: number) =>
    marks[i] === "ignore" && /[A-Za-z0-9]/.test(sample[i])
    && peers.length > 1 && peers.some((a) => a[i] !== sample[i]);
  for (let i = space.start - 1; i >= 0 && varies(i); i--) marks[i] = "space";
  for (let i = end(space); i < sample.length && varies(i); i++) marks[i] = "space";
  return marks;
}
